import { PaymentStatus } from "client";
import { SUPERSEDED_PAYMENT_STATUSES } from "services/virtualOrderStatus";

/** Non-superseded payment statuses — mirrors `getActivePayments` in virtualOrderStatus. */
export const getActivePaymentStatuses = (
  statuses: PaymentStatus[]
): PaymentStatus[] =>
  statuses.filter((status) => !SUPERSEDED_PAYMENT_STATUSES.includes(status));

/** Mirrors `isAnyPaymentPaid` — any PAID row counts, including after failed retries. */
export const isAnyPaid = (statuses: PaymentStatus[]): boolean =>
  statuses.includes(PaymentStatus.PAID);

/** Active (non-superseded) AWAITING_PAYMENT — mirrors dashboard pending bucket. */
export const hasActiveAwaitingPayment = (statuses: PaymentStatus[]): boolean =>
  getActivePaymentStatuses(statuses).includes(PaymentStatus.AWAITING_PAYMENT);

/** Mirrors `hasOnlySupersededPayments` in buildPaymentSettlementExpressions. */
export const hasOnlySupersededPayments = (statuses: PaymentStatus[]): boolean =>
  statuses.length > 0 && getActivePaymentStatuses(statuses).length === 0;

export type PaidTicketPaymentBucket = "paid" | "pending" | "failed" | "none";

/** Mirrors dashboard `isFreeTicket` — orderItem.total when set, else bookableItem.price. */
export const isFreeTicketItem = (
  orderItemTotal: number | null,
  bookablePrice: number | null
): boolean => {
  if (orderItemTotal !== null) return orderItemTotal <= 0;
  return (bookablePrice ?? 0) <= 0;
};

/** Mirrors dashboard `isPaidTicket` — orderItem.total when set, else bookableItem.price. */
export const isPaidTicketItem = (
  orderItemTotal: number | null,
  bookablePrice: number | null
): boolean => {
  if (orderItemTotal !== null) return orderItemTotal > 0;
  return (bookablePrice ?? 0) > 0;
};

/**
 * Classifies confirmed paid-ticket order items for dashboard metrics.
 * Free tickets (item total <= 0) are excluded from paid/pending/failed buckets.
 */
export const classifyPaidTicketPayment = (
  paymentStatuses: PaymentStatus[],
  orderItemTotal: number | null,
  bookablePrice: number | null
): PaidTicketPaymentBucket => {
  if (isFreeTicketItem(orderItemTotal, bookablePrice)) return "none";
  if (isAnyPaid(paymentStatuses)) return "paid";
  if (hasActiveAwaitingPayment(paymentStatuses)) return "pending";
  if (hasOnlySupersededPayments(paymentStatuses)) return "failed";
  return "none";
};
